import { type FC, useEffect, useMemo, useState } from "react";

import {
  endOfLocalDayMs,
  startOfLocalDayMs,
  toLocalDateInputValue,
} from "../../utils/dateFilters";
import { MS_IN_DAY } from "../../utils/usageConstants";
import { Badge } from "./Badge";
import { Button } from "./Button";
import { FormInput } from "./forms/FormInput";
import { Text } from "./Text";

export interface FilterField {
  key: string;
  label: string;
  placeholder?: string;
  helperText?: string;
  type?: "text" | "date" | "number";
  value: string;
  onChange: (value: string) => void;
}

export interface FiltersToolbarProps {
  isLoading: boolean;
  activeFilterCount: number;
  onRefresh: () => void;
  onReset: () => void;
  fromTimestampMs: number;
  toTimestampMs: number;
  onSetDateRange: (from: number, to: number) => void;
  fields: FilterField[];
}

const QUICK_RANGES = [
  { days: 7, label: "7D" },
  { days: 30, label: "30D" },
  { days: 90, label: "90D" },
] as const;

export const FiltersToolbar: FC<FiltersToolbarProps> = ({
  isLoading,
  activeFilterCount,
  onRefresh,
  onReset,
  fromTimestampMs,
  toTimestampMs,
  onSetDateRange,
  fields,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [fromInput, setFromInput] = useState(() =>
    toLocalDateInputValue(fromTimestampMs),
  );
  const [toInput, setToInput] = useState(() =>
    toLocalDateInputValue(toTimestampMs),
  );

  useEffect(() => {
    setFromInput(toLocalDateInputValue(fromTimestampMs));
  }, [fromTimestampMs]);

  useEffect(() => {
    setToInput(toLocalDateInputValue(toTimestampMs));
  }, [toTimestampMs]);

  const filtersActive = useMemo(
    () => activeFilterCount > 0,
    [activeFilterCount],
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Always visible: Quick controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Text variant="label">Range</Text>
          {QUICK_RANGES.map((range) => (
            <Button
              key={range.label}
              variant="outline"
              size="sm"
              onClick={() => {
                const now = Date.now();
                const to = now;
                const from = Math.max(0, now - range.days * MS_IN_DAY);
                setFromInput(toLocalDateInputValue(from));
                setToInput(toLocalDateInputValue(to));
                onSetDateRange(from, to);
              }}
            >
              {range.label}
            </Button>
          ))}
          <div className="ml-2 h-4 w-px bg-gray-200 dark:bg-white/10" />
          <Button
            variant={isExpanded ? "secondary" : "outline"}
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="gap-1.5"
          >
            <svg
              className={`h-3.5 w-3.5 transition-transform ${isExpanded ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="info" size="sm" className="ml-1">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isLoading && (
            <Badge variant="info" size="md" className="animate-pulse">
              Updating…
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <svg
              className="mr-1.5 h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </Button>
          {filtersActive && (
            <Button variant="ghost" size="sm" onClick={onReset}>
              Clear filters
            </Button>
          )}
        </div>
      </div>

      {/* Collapsible filter grid */}
      <div
        className={`grid overflow-hidden transition-all duration-200 ease-in-out ${
          isExpanded
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="min-h-0">
          <div className="rounded-none border border-slate-300 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <FormInput
                type="date"
                label="From"
                value={fromInput}
                onChange={(event) => {
                  const next = event.target.value;
                  setFromInput(next);
                  const start = startOfLocalDayMs(next);
                  if (start === null) {
                    return;
                  }
                  onSetDateRange(start, Math.max(start, toTimestampMs));
                }}
              />
              <FormInput
                type="date"
                label="To"
                value={toInput}
                onChange={(event) => {
                  const next = event.target.value;
                  setToInput(next);
                  const end = endOfLocalDayMs(next);
                  if (end === null) {
                    return;
                  }
                  onSetDateRange(Math.min(fromTimestampMs, end), end);
                }}
              />
              {fields.map((field) => (
                <FormInput
                  key={field.key}
                  type={field.type ?? "text"}
                  label={field.label}
                  placeholder={field.placeholder}
                  helperText={field.helperText}
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                />
              ))}
            </div>

            <div className="mt-3 flex justify-end">
              <Button variant="ghost" size="sm" onClick={onReset}>
                Reset all
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
